/**
 * Demo Hook
 * 
 * Custom hook for managing demo state and interactions.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { DemoAPI } from '../services/demo-api';
import { DemoSession } from '../contexts/DemoContext';
import { useDemoDataConnection } from '../contexts/DemoDataConnectionContext';
import { useUser } from '../contexts/UserContext';

interface UseDemoReturn {
  currentSession: DemoSession | null;
  currentScene: number;
  sceneData: Record<string, any> | null;
  isLoading: boolean;
  error: string | null;
  processUserInput: (input: string) => Promise<void>;
  advanceScene: () => Promise<any>;
  handleCardAction: (action: string, payload: any) => Promise<void>;
}

export const useDemo = (): UseDemoReturn => {
  const { currentDemoUser } = useDemoDataConnection();
  const { user: regularUser } = useUser();
  const [currentSession, setCurrentSession] = useState<DemoSession | null>(null);
  const [currentScene, setCurrentScene] = useState(0);
  const [sceneData, setSceneData] = useState<Record<string, any> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Use ref to prevent duplicate initialization in React Strict Mode
  const initializingRef = useRef(false);
  const initializedUserRef = useRef<string | undefined | null>(null);

  // Initialize demo session (only once per user)
  useEffect(() => {
    const initializeDemo = async () => {
      // Use regular user ID instead of demo user ID to ensure threads are visible in Chat UI
      const userId = regularUser?.id || currentDemoUser?.id;
      
      // Prevent duplicate initialization
      if (initializingRef.current) {
        console.log('Demo initialization already in progress, skipping...');
        return;
      }
      
      // Check if we already initialized for this user
      if (initializedUserRef.current === userId) {
        console.log('Demo already initialized for user:', userId);
        return;
      }
      
      // Check if we already have a session for this user
      if (currentSession && currentSession.user_id === userId) {
        console.log('Demo session already exists for user:', userId);
        initializedUserRef.current = userId;
        setIsLoading(false);
        return;
      }
      
      try {
        initializingRef.current = true;
        setIsLoading(true);
        setError(null);

        console.log('Initializing demo for user:', userId);

        // Create new demo session with regular user ID
        const session = await DemoAPI.createSession(userId);
        setCurrentSession(session);
        initializedUserRef.current = userId;

        // Get initial scene info
        const sceneInfo = await DemoAPI.getCurrentSceneInfo(session.session_key);
        setSceneData(sceneInfo);

        // Set initial scene
        setCurrentScene(session.current_scene);

        console.log('Demo initialized successfully:', session);
        console.log('Initial scene info:', sceneInfo);
      } catch (err) {
        console.error('Failed to initialize demo:', err);
        setError(err instanceof Error ? err.message : 'Failed to initialize demo');
        initializedUserRef.current = null; // Reset on error to allow retry
      } finally {
        setIsLoading(false);
        initializingRef.current = false;
      }
    };

    if ((regularUser || currentDemoUser) && !currentSession) {
      initializeDemo();
    }
  }, [regularUser?.id, currentDemoUser?.id, currentSession]); // Added currentSession back to dependencies

  // Process user input
  const processUserInput = useCallback(async (input: string) => {
    if (!currentSession) {
      throw new Error('No demo session available');
    }

    try {
      setError(null);
      const result = await DemoAPI.processUserInput(currentSession.session_key, input);
      
      // Update session data
      if (result.session) {
        setCurrentSession(result.session);
        setCurrentScene(result.session.current_scene);
      }

      // Update scene data with latest info
      const sceneInfo = await DemoAPI.getCurrentSceneInfo(currentSession.session_key);
      setSceneData(sceneInfo);

      console.log('User input processed:', result);
      console.log('Updated scene info:', sceneInfo);
    } catch (err) {
      console.error('Failed to process user input:', err);
      setError(err instanceof Error ? err.message : 'Failed to process user input');
      throw err;
    }
  }, [currentSession]);

  // Advance to next scene
  const advanceScene = useCallback(async () => {
    if (!currentSession) {
      throw new Error('No demo session available');
    }

    try {
      setError(null);
      
      // Skip validation for demo flow - just advance to next scene
      // Users can trigger scene advancement via action buttons without meeting all requirements
      // const sceneInfo = await DemoAPI.getCurrentSceneInfo(currentSession.session_key);
      // if (!sceneInfo.is_complete) {
      //   const missingInputs = sceneInfo.missing_inputs || [];
      //   throw new Error(`Scene requires input: ${missingInputs.join(', ')}`);
      // }

      const result = await DemoAPI.advanceScene(currentSession.session_key);
      
      // Update session data
      if (result.session) {
        setCurrentSession(result.session);
        setCurrentScene(result.session.current_scene);
      }

      // Update scene data
      setSceneData(result);

      console.log('✅ [useDemo] Scene advanced:', result);
      
      // If a new thread was created, navigate to it
      if (result.thread_id) {
        console.log('🔄 [useDemo] New thread created for next scene:', result.thread_id);
        // The ChatContext will automatically refresh threads and select the new one
      }
      
      // Return result so caller can access thread_id
      return result;
    } catch (err) {
      console.error('❌ [useDemo] Failed to advance scene:', err);
      setError(err instanceof Error ? err.message : 'Failed to advance scene');
      throw err;
    }
  }, [currentSession]);

  // Handle card actions
  const handleCardAction = useCallback(async (action: string, payload: any) => {
    if (!currentSession) {
      throw new Error('No demo session available');
    }

    try {
      setError(null);
      const result = await DemoAPI.handleCardAction(currentSession.session_key, action, payload);
      
      // Update session data
      if (result.session) {
        setCurrentSession(result.session);
        setCurrentScene(result.session.current_scene);
      }

      console.log('Card action handled:', result);
    } catch (err) {
      console.error('Failed to handle card action:', err);
      setError(err instanceof Error ? err.message : 'Failed to handle card action');
      throw err;
    }
  }, [currentSession]);

  return {
    currentSession,
    currentScene,
    sceneData,
    isLoading,
    error,
    processUserInput,
    advanceScene,
    handleCardAction
  };
};

export default useDemo;
